import React, { useEffect, useState } from 'react'
import { MqDesktop, MqTablet } from 'streamr-ui/css/consts'
import styled, { css, keyframes } from 'styled-components'
import { HeroHeading1, HeroHeading2, HeroSkewedTile } from '../../../utils/css'
import { getStakingStats } from '../../../utils/graphs'
import { SkewedTile, SkewedTileList } from '../../SkewedTile'
import Apys from '../assets/apys.png'
import Apys2x from '../assets/apys@2x.png'
import Operators from '../assets/operators.png'
import Operators2x from '../assets/operators@2x.png'
import Reward from '../assets/reward.png'
import Reward2x from '../assets/reward@2x.png'
import Stakes from '../assets/stakes.png'
import Stakes2x from '../assets/stakes@2x.png'
import { getActiveOperatorCount } from '../helpers/getActiveOperatorCount'

export function StakeAndEarnHero() {
    const [operatorCount, setOperatorCount] = useState(undefined)

    const [stakeStats, setStakeStats] = useState()

    const totalStake = stakeStats?.tvl

    const apy = stakeStats?.apy

    useEffect(function fetchOperatorCount() {
        let mounted = true

        void (async () => {
            try {
                const count = await getActiveOperatorCount()

                if (mounted) {
                    setOperatorCount(count)
                }
            } catch (e) {
                console.warn('Failed to fetch the active operator count', e)
            }
        })()

        return () => {
            mounted = false
        }
    }, [])

    useEffect(function updateStakeStats() {
        let mounted = true

        void (async () => {
            try {
                const stats = await getStakingStats()

                if (mounted) {
                    setStakeStats(stats)
                }
            } catch (_) {
                // Noop.
            }
        })()

        return () => {
            mounted = false
        }
    }, [])

    return (
        <StakeAndEarnHeroRoot>
            <Background2 />
            <Background1 />
            <Background0 />
            <Content>
                <h1>
                    Stream data.
                    <br />
                    Earn <em>$DATA</em>.
                </h1>
                <h2>
                    Join the ranks of Streamr, where a global community powers a&nbsp;decentralized
                    network. Help secure the network by deploying a&nbsp;Streamr node and earn
                    through your idle&nbsp;bandwidth.
                </h2>
                <Tiles>
                    <img src={Reward} srcSet={`${Reward2x} 2x`} alt="Earn" />
                    <HeroSkewedTile />
                    <HeroSkewedTile
                        style={{
                            '--ty': '-128%',
                            opacity: 0.3,
                        }}
                    />
                    <HeroSkewedTile
                        style={{
                            '--tx': '-216%',
                            '--ty': '-128%',
                            opacity: 0.15,
                        }}
                    />
                    <HeroSkewedTile
                        style={{
                            '--tx': '-216%',
                            '--ty': '-256%',
                            opacity: 0.2,
                        }}
                    />
                    <HeroSkewedTile
                        style={{
                            '--tx': '-216%',
                            opacity: 0.2,
                        }}
                    />
                    <HeroSkewedTile
                        style={{
                            '--tx': '108%',
                            '--ty': '-64%',
                            opacity: 0.2,
                        }}
                    />
                    <HeroSkewedTile
                        style={{
                            '--tx': '-108%',
                            '--ty': '-64%',
                            opacity: 0.25,
                        }}
                    />
                    <HeroSkewedTile
                        style={{
                            '--tx': '108%',
                            '--ty': '64%',
                            opacity: 0.3,
                        }}
                    />
                    <HeroSkewedTile
                        style={{
                            '--tx': '-108%',
                            '--ty': '64%',
                            opacity: 0.2,
                        }}
                    />
                </Tiles>
                <Stats>
                    <Stat>
                        <StatDetails $loading={operatorCount == null}>
                            <h4>{formatInt(operatorCount || 0)}</h4>
                            <p>Active Operators</p>
                        </StatDetails>
                        <div>
                            <OperatorsImg
                                width="105"
                                height="123"
                                src={Operators}
                                srcSet={`${Operators2x} 2x`}
                                alt="Operators"
                            />
                        </div>
                    </Stat>
                    <Stat>
                        <StatDetails $loading={totalStake == null}>
                            <h4>{formatFloat((totalStake || 0) / 10 ** 6)}M</h4>
                            <p>$DATA staked on Operators</p>
                        </StatDetails>
                        <div>
                            <StakesImg
                                width="95"
                                height="111"
                                src={Stakes}
                                srcSet={`${Stakes2x} 2x`}
                                alt="Stake"
                            />
                        </div>
                    </Stat>
                    <Stat>
                        <StatDetails $loading={apy == null}>
                            <h4>{formatFloat(Number(apy || 0))}%</h4>
                            <p>Operator APY</p>
                        </StatDetails>
                        <div>
                            <ApysImg
                                width="153"
                                height="106"
                                src={Apys}
                                srcSet={`${Apys2x} 2x`}
                                alt="APY"
                            />
                        </div>
                    </Stat>
                </Stats>
            </Content>
        </StakeAndEarnHeroRoot>
    )
}

function formatFloat(value) {
    return value.toFixed(2).replace(/0+$/, '').replace(/\.$/, '')
}

function formatInt(value) {
    return value
        .toFixed(0)
        .split('')
        .reverse()
        .reduce((memo, t) => `${t}${memo.length && memo.length % 3 === 0 ? ',' : ''}${memo}`, '')
}

const Background0 = styled.div`
    position: absolute;
    height: calc(100% + 80px);
    width: calc(100% + 80px);
    top: 0px;
    left: 0px;
    background-image: radial-gradient(
        circle at var(--RingX, 50%) calc(100% + var(--RingOffsetY)),
        transparent 0px,
        transparent calc(var(--RingRadius) - var(--RingWeight) - 1px),
        #ffffff calc(var(--RingRadius) - var(--RingWeight)),
        #ffffff var(--RingRadius),
        transparent calc(var(--RingRadius) + 1px)
    );
    filter: blur(10px);
    transform: translateX(-40px);
`

const Background1 = styled.div`
    position: absolute;
    height: calc(100% + 80px);
    width: calc(100% + 80px);
    top: 0px;
    left: 0px;
    background-image: radial-gradient(
        circle at var(--RingX, 50%) calc(100% + var(--RingOffsetY)),
        transparent 0px,
        transparent calc(var(--RingRadius) - var(--RingWeight) - 1px),
        #0ea5ff calc(var(--RingRadius) - var(--RingWeight)),
        #0ea5ff var(--RingRadius),
        transparent calc(var(--RingRadius) + 1px)
    );
    filter: blur(25px);
    transform: translateX(-40px);
`

const Background2 = styled.div`
    position: absolute;
    height: calc(100% + 80px);
    width: calc(100% + 80px);
    top: 0px;
    left: 0px;
    background-image: radial-gradient(
        circle at var(--RingX, 50%) calc(100% + var(--RingOffsetY)),
        transparent 0px,
        transparent calc(var(--RingRadius) - var(--RingWeight2) - 1px),
        #3e4fec calc(var(--RingRadius) - var(--RingWeight2)),
        #3e4fec var(--RingRadius),
        transparent calc(var(--RingRadius) + 1px)
    );
    filter: blur(30px);
    transform: translateX(-40px);
`

const Tiles = styled(SkewedTileList)`
    --imgDy: -4%;
    --imgWidth: 240px;
    margin: 200px 0 180px;

    ${SkewedTile} {
        opacity: 0.7;
    }

    @media ${MqTablet} {
        --imgWidth: 300px;
        margin: 240px 0 280px;
    }

    @media ${MqDesktop} {
        --imgWidth: 350px;
        margin: 236px 0 440px;
    }
`

const OperatorsImg = styled.img`
    height: auto;
    width: 88px;

    @media ${MqTablet} {
        width: auto;
    }
`

const StakesImg = styled.img`
    height: auto;
    width: 76px;

    @media ${MqTablet} {
        width: auto;
    }
`

const ApysImg = styled.img`
    height: auto;
    transform: translate(-10%, 5%);
    width: 122px;

    @media ${MqTablet} {
        transform: translate(15%, 0px);
        width: auto;
    }

    @media ${MqDesktop} {
        transform: translate(-10%, 5%);
    }
`

const pulse = keyframes`
    0%, 100% {
        opacity: 0.3;
    }

    50% {
        opacity: 1;
    }
`

const StatDetails = styled.div`
    flex-grow: 1;

    ${({ $loading = false }) =>
        $loading &&
        css`
            h4 {
                animation: ${pulse} 1s infinite;
            }
        `}
`

const Stat = styled.div`
    display: flex;
    flex-direction: column;
    border-radius: 20px;
    box-shadow: inset 0 0 0 1px rgba(255, 255, 255, 0.1);
    background: linear-gradient(rgba(0, 0, 0, 0.13) 0%, rgba(28, 59, 151, 0) 100%);
    backdrop-filter: blur(75px);
    padding: 48px;
    box-sizing: border-box;

    img {
        margin-top: 48px;
    }

    @media ${MqTablet} {
        flex-direction: row;
        height: auto;

        img {
            margin-top: 0;
        }
    }

    @media ${MqDesktop} {
        flex-direction: column;
        height: 480px;
    }
`

const Stats = styled.div`
    display: grid;
    gap: 28px;
    grid-template-columns: 1fr;
    margin: 0 auto;
    padding: 0 32px;
    box-sizing: border-box;
    max-width 100%;
    width: 588px;

    h4 {
        font-size: 32px;
        line-height: 40px;
        margin: 0 0 0.4em;
    }

    p {
        font-size: 16px;
        line-height: 24px;
        margin: 0 auto;
    }

    img {
        display: block;
    }

    @media ${MqTablet} {
        h4 {
            font-size: 40px;
            line-height: 48px;
        }

        p {
            font-size: 22px;
            line-height: 32px;
        }
    }

    @media ${MqDesktop} {
        grid-template-columns: 1fr 1fr 1fr;
        width: 1224px;

        h4 {
            font-size: 48px;
            line-height: 56px;
        }
    }
`

const Content = styled.div`
    color: #ffffff;
    padding: 272px 0 0;
    position: relative;

    h1 {
        ${HeroHeading1}
    }

    h1 em {
        color: #f65f0a;
        font-style: normal;
    }

    h2 {
        ${HeroHeading2}
    }
`

const StakeAndEarnHeroRoot = styled.div`
    --RingRadius: 430px;
    --RingWeight: 15px;
    --RingWeight2: 24px;
    --RingOffsetY: calc(var(--RingRadius) - 1100px);
    --RingX: calc(50% + 360px);

    background: #121622;
    background-image: radial-gradient(
            circle at 15% -15%,
            rgba(62, 79, 236, 0.25) 0,
            rgba(62, 79, 236, 0) 40%
        ),
        radial-gradient(circle at 90% -5%, rgba(62, 79, 236, 0.25) 0, rgba(62, 79, 236, 0) 25%),
        radial-gradient(circle at -15% 25%, rgba(62, 79, 236, 0.2) 0, rgba(62, 79, 236, 0) 25%);
    margin-top: -4.5rem;
    padding-bottom: 120px;
    position: relative;
    overflow: hidden;

    @media ${MqTablet} {
        --RingRadius: 1000px;
        --RingOffsetY: calc(var(--RingRadius) - 920px);
        --RingX: calc(50% + 790px);

        margin-top: -4rem;
    }

    @media ${MqDesktop} {
        --RingRadius: 1800px;
        --RingOffsetY: calc(var(--RingRadius) - 560px);
        --RingX: 50%;

        margin-top: -4.5rem;
        padding-bottom: 256px;
    }
`
